import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getAllCapteurByProjectId } from 'services/api';
import DataGrid from 'composant/UI/DataGrid/DataGrid';
import styles from './SensorsListInput.module.scss';

interface SensorItem {
  sensor_id: number;
  sensor_name: string;
  view_type?: string;
  remarks?: string;
}

interface SensorsListInputProps {
  argument: any;
  onChange: (field: string, value: any) => void;
  selectionMode?: 'single' | 'multiple';
  showInfo?: boolean;
  projectId?: string | number;
}

const SensorsListInput: React.FC<SensorsListInputProps> = ({
  argument,
  onChange,
  selectionMode = 'multiple',
  projectId,
}) => {
  const projectIds = useSelector(
    (state: any) => state.authentifie.filtre_projects_list_id
  );
  const [availableSensors, setAvailableSensors] = useState<SensorItem[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<SensorItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getSensors = async () => {
      setLoading(true);
      const response = await getAllCapteurByProjectId({
        projects_id: projectId ? [projectId] : projectIds,
      });
      setAvailableSensors(response.data.sensors_list || []);
      setSelectedSensors([]);
      setLoading(false);
    };
    getSensors();
  }, [projectId, projectIds]);

  useEffect(() => {
    if (argument.valeurParDefaut || argument.value) {
      const value = argument.valeurParDefaut || argument.value;
      const isDevuiList = argument.type === 'DevuiList';

      let identifiers: (number | string)[] = [];

      try {
        if (typeof value === 'string') {
          if (value.startsWith('[')) {
            const parsed = JSON.parse(value);
            if (isDevuiList) {
              identifiers = parsed.map((id: any) => String(id));
            } else {
              identifiers = parsed.map((id: any) => parseInt(id, 10));
            }
          } else {
            if (isDevuiList) {
              identifiers = value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '');
            } else {
              identifiers = value
                .split(',')
                .map((id) => parseInt(id.trim(), 10))
                .filter((id) => !isNaN(id));
            }
          }
        } else if (Array.isArray(value)) {
          identifiers = isDevuiList
            ? value.map((id) => String(id))
            : value.map((id) => parseInt(id, 10));
        }
      } catch (error) {
        console.error('Error parsing sensor identifiers:', error);
      }

      const selected = availableSensors.filter((sensor) => {
        if (isDevuiList) {
          return identifiers.includes(sensor.remarks || '');
        } else {
          return identifiers.includes(sensor.sensor_id);
        }
      });
      setSelectedSensors(selected);
    }
  }, [
    argument.valeurParDefaut,
    argument.value,
    argument.type,
    availableSensors,
  ]);

  const handleSelectionChange = useCallback(
    (sensors: SensorItem[]) => {
      setSelectedSensors(sensors);

      const isDevuiList = argument.type === 'DevuiList';

      const identifiers = isDevuiList
        ? sensors.map((s) => s.remarks || '')
        : sensors.map((s) => s.sensor_id);

      const formattedValue = JSON.stringify(identifiers);

      onChange('valeurParDefaut', formattedValue);
    },
    [argument.type, onChange]
  );

  const columns = useMemo(
    () => [
      {
        field: 'sensor_id',
        headerName: 'ID du capteur',
        width: 140,
        sortable: true,
        align: 'center' as const,
        headerAlign: 'center' as const,
      },
      {
        field: 'sensor_name',
        headerName: 'Nom du capteur',
        width: 240,
        sortable: true,
        flex: 1,
        align: 'center' as const,
        headerAlign: 'center' as const,
      },
    ],
    []
  );

  const selectionModel = useMemo<any[]>(
    () => selectedSensors.map((sensor) => sensor.sensor_id),
    [selectedSensors]
  );

  const handleSelectionModelChange = useCallback(
    (newSelection: any[]) => {
      if (selectionMode === 'single' && newSelection.length > 1) {
        newSelection = [newSelection[newSelection.length - 1]];
      }
      const selected = availableSensors.filter((sensor) =>
        newSelection.includes(sensor.sensor_id)
      );
      handleSelectionChange(selected);
    },
    [availableSensors, handleSelectionChange, selectionMode]
  );

  return (
    <div>
      <div className={styles.headerSection}>
        <div>
          <h6 className={styles.headerTitle}>
            Sélection des capteurs
            {selectionMode === 'single' && ' (sélection unique)'}
          </h6>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingMessage}>Chargement des capteurs...</div>
      ) : availableSensors.length === 0 ? (
        <div className={styles.emptyMessage}>
          Aucun capteur disponible pour ce projet.
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <DataGrid
            rows={availableSensors}
            columns={columns}
            getRowId={(row: any) => row.sensor_id}
            checkboxSelection={selectionMode === 'multiple'}
            disableRowSelectionOnClick
            onRowSelectionModelChange={handleSelectionModelChange}
            rowSelectionModel={selectionModel as any}
            disableMultipleRowSelection={selectionMode === 'single'}
            pageSizeOptions={[10, 25, 50]}
            initialPageSize={10}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default SensorsListInput;
